// TODO(joe): public/pilot-logo-mark.svg exists but no `pilot` project in DB. Confirm or delete.
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Frown, ShieldAlert } from 'lucide-react';
import { Chip, Kicker, LogoMark } from '../components/ui';
import { supabase } from '../lib/supabase';
import { isNetworkError } from '../lib/isNetworkError';
import { projectsFallback } from '../data/projectsFallback';
import { FallbackBanner } from '../components/FallbackBanner';

type ProjectStatus = 'active' | 'archived' | 'coming_soon' | 'private';
type Category = 'product' | 'infra' | 'tool' | 'civic' | 'education';

interface ProjectRow {
  slug: string;
  name: string;
  tagline: string | null;
  description: string | null;
  status: ProjectStatus | null;
  featured: boolean | null;
  client_type: string | null;
  year_completed: number | null;
  logo_url: string | null;
  /** Schema gap: projects has no `category` column. Admin may store it in `metrics->>'category'`
   *  as a forward-compatible path; until then everything defaults to 'product'. */
  metrics: Record<string, unknown> | null;
}

type Filter = 'all' | Category;

const FILTERS: Filter[] = ['all', 'product', 'infra', 'tool', 'civic', 'education'];

function categoryOf(p: ProjectRow): Category {
  const raw = (p.metrics && typeof p.metrics === 'object' ? (p.metrics as Record<string, unknown>).category : null) as
    | string
    | null
    | undefined;
  if (raw === 'product' || raw === 'infra' || raw === 'tool' || raw === 'civic' || raw === 'education') {
    return raw;
  }
  return 'product';
}

function clientOf(p: ProjectRow): string {
  // SerVet Digital IS the company — it has no client above it.
  if (p.client_type === 'company') return '—';
  if (p.client_type === 'startup') return 'SerVet Digital';
  if (p.client_type === 'federal') return 'Federal (contract)';
  if (p.client_type === 'enterprise') return 'Enterprise';
  return 'self-initiated';
}

function statusLabel(s: ProjectStatus | null | undefined): string {
  if (s === 'active') return 'live';
  if (s === 'archived') return 'archived';
  if (s === 'coming_soon') return 'early dev';
  return '—';
}

const WorkPage: React.FC = () => {
  const [projects, setProjects] = useState<ProjectRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [usingFallback, setUsingFallback] = useState(false);
  const [filter, setFilter] = useState<Filter>('all');
  const [loadAttempt, setLoadAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const fetchProjects = async (attempt: number): Promise<void> => {
      try {
        // TODO: if projects table changes materially, re-sync src/data/projectsFallback.ts
        const { data, error: err } = await supabase
          .from('projects')
          .select(
            'slug, name, tagline, description, status, featured, client_type, year_completed, logo_url, metrics'
          )
          .neq('status', 'private')
          .order('featured', { ascending: false })
          .order('year_completed', { ascending: false, nullsFirst: false });

        if (cancelled) return;
        if (err) throw new Error(err.message);

        setProjects((data ?? []) as ProjectRow[]);
        setError(null);
        setUsingFallback(false);
      } catch (e) {
        if (cancelled) return;
        // One quiet auto-retry for transient network failures (the common
        // "TypeError: Failed to fetch" on flaky connections / captive portals).
        if (attempt < 1) {
          await new Promise(r => setTimeout(r, 1200));
          if (cancelled) return;
          return fetchProjects(attempt + 1);
        }
        if (isNetworkError(e)) {
          setProjects(projectsFallback as unknown as ProjectRow[]);
          setUsingFallback(true);
          setError(null);
          return;
        }
        const message = e instanceof Error ? e.message : String(e);
        setError(message);
      }
    };

    setProjects(null);
    setError(null);
    setUsingFallback(false);
    fetchProjects(0);

    return () => {
      cancelled = true;
    };
  }, [loadAttempt]);

  const retry = () => setLoadAttempt(n => n + 1);

  const { products, company } = useMemo(() => {
    if (!projects) return { products: [], company: null as ProjectRow | null };
    const filtered =
      filter === 'all' ? projects : projects.filter(p => categoryOf(p) === filter);
    const company = filtered.find(p => p.slug === 'servet-digital') ?? null;
    const products = filtered.filter(p => p.slug !== 'servet-digital');
    return { products, company };
  }, [projects, filter]);

  const visibleCount = products.length + (company ? 1 : 0);

  const live = projects ? projects.filter(p => p.status === 'active').length : 0;
  const total = projects?.length ?? 0;

  return (
    <div className="max-w-content mx-auto px-s6 md:px-s12 py-s12">
      {usingFallback && <FallbackBanner />}
      <div className="mb-s8">
        <Kicker>work</Kicker>
        <h1 className="font-mono text-[36px] font-medium tracking-[-0.8px] mt-s3 mb-2 text-text">
          some of the things I've shipped.
        </h1>
        <p className="text-[14px] text-text-muted max-w-[520px]">
          {projects === null
            ? 'loading…'
            : `${total} projects. ${live} live. The rest taught me more.`}
        </p>
        <div className="flex flex-wrap gap-1.5 mt-s5">
          {FILTERS.map(f => (
            <Chip key={f} active={filter === f} onClick={() => setFilter(f)}>
              {f}
            </Chip>
          ))}
        </div>
      </div>

      {error && (
        <div className="flex flex-col items-center justify-center text-center py-s16 px-s5 border border-border rounded-xs bg-surface">
          <div className="relative mb-s5" aria-hidden>
            <ShieldAlert
              size={52}
              strokeWidth={1.5}
              className="text-accent-alt-hover"
            />
            <Frown
              size={22}
              strokeWidth={1.8}
              className="absolute -bottom-1 -right-2 text-text-muted bg-bg rounded-full p-0.5"
            />
          </div>
          <h2 className="font-mono text-[18px] font-medium text-text tracking-[-0.3px] mb-s2">
            looks like you're behind a firewall
          </h2>
          <p className="text-[13px] text-text-muted leading-[1.6] max-w-[440px] mb-s5">
            the projects live on a hosted backend your network isn't letting
            through — common on corporate wifi, captive portals, or strict
            DNS. try again in a minute, or drop me a line directly.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-s3">
            <button
              type="button"
              onClick={retry}
              className="inline-flex items-center gap-2 px-s3 py-2 rounded-xs bg-accent text-accent-ink font-mono text-[11px] uppercase tracking-[0.4px] hover:brightness-110 transition"
            >
              try again
            </button>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-s3 py-2 rounded-xs border border-border text-text-muted font-mono text-[11px] uppercase tracking-[0.4px] hover:text-text hover:border-border-hi transition-colors"
            >
              email me instead →
            </Link>
          </div>
          <div className="font-mono text-[10px] text-text-dim mt-s6 max-w-[440px] truncate">
            // {error}
          </div>
        </div>
      )}

      {!error && projects === null && (
        <div className="space-y-s3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-[72px] bg-surface border border-border rounded-xs animate-pulse" />
          ))}
        </div>
      )}

      {!error && projects && projects.length === 0 && (
        <div className="font-mono text-[11px] text-text-dim">
          // no projects loaded &middot; check supabase connection
        </div>
      )}

      {!error && projects && projects.length > 0 && (
        <div>
          <div className="hidden md:grid gap-s4 py-2.5 border-b border-border font-mono text-[10px] text-text-dim uppercase tracking-[1px] [grid-template-columns:60px_1fr_200px_120px_80px_24px]">
            <span>#</span>
            <span>project</span>
            <span>client</span>
            <span>year</span>
            <span>status</span>
            <span />
          </div>

          {products.map((p, i) => (
            <Link
              key={p.slug}
              to={`/work/${p.slug}`}
              className="grid gap-s4 py-s5 border-b border-border items-center hover:bg-surface transition-colors group [grid-template-columns:40px_1fr_24px] md:[grid-template-columns:60px_1fr_200px_120px_80px_24px]"
            >
              <span className="font-mono text-text-dim text-[12px]">
                {String(i + 1).padStart(2, '0')}
              </span>
              <div className="min-w-0 flex items-center gap-s3">
                <LogoMark name={p.name} logoUrl={p.logo_url} size={32} />
                <div className="min-w-0">
                  <div className="text-[18px] font-semibold tracking-[-0.3px] text-text truncate">
                    {p.name}
                  </div>
                  <div className="text-[13px] text-text-muted mt-0.5 line-clamp-2">
                    {p.tagline || (p.description ? p.description.slice(0, 120) : '')}
                  </div>
                </div>
              </div>
              <span className="hidden md:inline font-mono text-[12px] text-text-muted truncate">
                {clientOf(p)}
              </span>
              <span className="hidden md:inline font-mono text-[12px] text-text-muted">
                {p.year_completed ?? '—'}
              </span>
              <span className="hidden md:inline">
                <Chip active={p.status === 'active'}>{statusLabel(p.status)}</Chip>
              </span>
              <span className="text-accent text-right group-hover:translate-x-0.5 transition-transform">↗</span>
            </Link>
          ))}

          {company && (
            <>
              <div
                aria-hidden
                className="pt-s8 pb-s2 font-mono text-[10px] text-text-dim uppercase tracking-[1.2px] kicker-line"
              >
                built under
              </div>
              <Link
                key={company.slug}
                to={`/work/${company.slug}`}
                className="grid gap-s4 py-s5 border-b border-border items-center hover:bg-surface transition-colors group [grid-template-columns:40px_1fr_24px] md:[grid-template-columns:60px_1fr_200px_120px_80px_24px]"
              >
                <span className="font-mono text-text-dim text-[12px]">
                  {String(products.length + 1).padStart(2, '0')}
                </span>
                <div className="min-w-0 flex items-center gap-s3">
                  <LogoMark name={company.name} logoUrl={company.logo_url} size={32} />
                  <div className="min-w-0">
                    <div className="text-[18px] font-semibold tracking-[-0.3px] text-text truncate">
                      {company.name}
                    </div>
                    <div className="text-[13px] text-text-muted mt-0.5 line-clamp-2">
                      {company.tagline ||
                        (company.description ? company.description.slice(0, 120) : '')}
                    </div>
                  </div>
                </div>
                <span className="hidden md:inline font-mono text-[12px] text-text-muted truncate">
                  {clientOf(company)}
                </span>
                <span className="hidden md:inline font-mono text-[12px] text-text-muted">
                  {company.year_completed ?? '—'}
                </span>
                <span className="hidden md:inline">
                  <Chip active={company.status === 'active'}>{statusLabel(company.status)}</Chip>
                </span>
                <span className="text-accent text-right group-hover:translate-x-0.5 transition-transform">↗</span>
              </Link>
            </>
          )}

          {visibleCount === 0 && (
            <div className="py-s10 text-center text-text-muted font-mono text-[12px]">
              no projects match this filter yet.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WorkPage;
